import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
} from "@mui/material";
import theme from "../../theme";
import SearchInput from "../../Shared/Components/SearchInput/SearchInput";
import {
  SearchToolTip,
  permissionsConstants,
} from "../../constants/AppConstants";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useButtonStyles from "../../styles/useButtonStyles";
import { CommonButton } from "../../Shared/Components/Buttons/CommonButton";
import {
  downloadFinanceReports,
  getFinanceReports,
  handleDownloadPredefinedCSV,
  handleUpload,
} from "../../services/ApiServices/FinanceReports";
import DownloadForOfflineRoundedIcon from "@mui/icons-material/DownloadForOfflineRounded";
import BasicModal from "../ui/BasicModal";
import { iconMapping } from "./IconMapping";
import { usePermissionGetter } from "../../Hooks/Permissions/userPermissionGetter";
import Loader from "../ui/Loader";

interface Report {
  category_name: string;
  category_icon: string;
  data: ReportData[];
}

interface ReportData {
  report_icon: string;
  report_name: string;
  report_url: string;
}

const FinanceReports = () => {
  const [file, setFile] = useState<File | null>(null);
  const buttonStyles = useButtonStyles();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Something went wrong!"
  );
  const [successMessage, setSuccessMessage] = useState<string>("");

  const { getUserPermission: getFinanceReportPermission, userPermission } =
    usePermissionGetter(
      permissionsConstants.finance_reports,
      permissionsConstants.finance_reports
    );

  const handleOpenImportDialog = () => {
    setOpenImportDialog(false);
  };
  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
    setLoading(false);
  };
  const handleImport = () => {
    setOpenImportDialog(true);
  };
  const handleDownload = () => {
    downloadFinanceReports(searchValue);
  };
  const handleUploadButtonClick = () => {
    try {
      handleUpload(
        file,
        setLoading,
        setShowModal,
        setUploadError,
        setErrorMessage,
        setSuccessMessage
      );
      setTimeout(() => {
        setOpenImportDialog(false);
      }, 5000);
    } catch (error) {}
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleSearch = () => {
    const value = searchInputRef.current?.value || "";
    const encodedValue = encodeURIComponent(value);
    setSearchValue(encodedValue);
  };

  const fetchData = async (searchQuery: string) => {
    setLoading(true);
    try {
      const response = await getFinanceReports(searchValue);
      setReports(response.value);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(searchValue);
  }, [searchValue]);

  return (
    <Grid
      borderRadius={"16px"}
      sx={{ background: theme.color?.background }}
      p={4}
      px={2}
    >
      <Grid>
        <Grid
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          px={3}
          borderBottom={`${theme?.border?.xsmall}${theme.color?.dividerLight}`}
        >
          <Grid mt={3} alignItems={"left"} pr={10} direction={"row"}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: theme?.font?.fontFamily.primary,
                fontWeight: theme.font?.fontWeight.thicker,
                fontSize: theme.font?.fontSize.large,
              }}
            >
              Finance Reports
            </Typography>
          </Grid>
          <Grid display={"flex"}>
            <Grid
              display={"flex"}
              alignItems={"center"}
              direction={"row"}
              pr={1}
            ></Grid>

            <Grid
              display={"flex"}
              justifyContent={"center"}
              px={0}
              py={3}
              direction={"row"}
            >
              {/* {getFinanceReportPermission("download") && (
                <CommonButton
                  buttonTitle="Download"
                  variant="text"
                  color="secondary"
                  onClick={handleDownload}
                  sx={{ ml: 2 }}
                />
              )}
              {getFinanceReportPermission("create") && (
                <CommonButton
                  buttonTitle="Add File"
                  variant="text"
                  color="secondary"
                  onClick={handleImport}
                  sx={{ ml: 2 }}
                />
              )} */}
              {openImportDialog && (
                <Dialog
                  open={openImportDialog}
                  onClose={handleCloseImportDialog}
                >
                  <>
                    <DialogTitle
                      display={"flex"}
                      justifyContent={"space-between"}
                    >
                      <DialogTitle sx={{ fontWeight: "600" }}>
                        Import CSV File
                      </DialogTitle>
                      <IconButton
                        aria-label="delete"
                        onClick={handleDownloadPredefinedCSV}
                        sx={{
                          color: theme.color?.secondary,
                        }}
                      >
                        <DownloadForOfflineRoundedIcon />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent>
                      <TextField
                        type="file"
                        onChange={handleFileChange}
                        sx={{
                          color: theme.color?.secondary,
                        }}
                        inputProps={{
                          accept: ".csv",
                        }}
                      />
                    </DialogContent>
                    <DialogActions
                      sx={{
                        marginBottom: 2,
                        justifyContent: "center",
                        padding: 2,
                      }}
                    >
                      <CommonButton
                        buttonTitle="Cancel"
                        variant="outlined"
                        color="secondary"
                        sx={{ width: "45%", mr: 2 }}
                        onClick={handleOpenImportDialog}
                      />
                      <Button
                        onClick={handleUploadButtonClick}
                        sx={{
                          ...buttonStyles.containedSecondary,
                          width: "45%",
                          ml: 2,
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="primary" />
                        ) : (
                          "Import"
                        )}
                      </Button>
                    </DialogActions>
                  </>
                </Dialog>
              )}
              <Grid display={"flex"} gap={3}>
                <SearchInput
                  HelpTip={SearchToolTip.FinanceReports}
                  onCallBack={handleSearch}
                  ref={searchInputRef}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {getFinanceReportPermission("read") && (
          <Grid
            container
            width={"100%"}
            px={2}
            mt={2}
            display={"flex"}
            flexDirection={"column"}
          >
            {loading ? (
              <Grid container justifyContent="center" alignItems="center">
                <Loader size={50} />
              </Grid>
            ) : (
              reports?.map((report, index) => (
                <Grid key={index} mb={2}>
                  <Typography
                    variant="h6"
                    mb={1}
                    sx={{
                      fontFamily: theme?.font?.fontFamily.primary,
                      fontWeight: theme.font?.fontWeight.thick,
                    }}
                  >
                    {/* {report.category_name} */}
                  </Typography>
                  {report.data.map((item, itemIndex) => (
                    <Grid key={itemIndex} mb={1}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                        boxShadow={1}
                        borderRadius={"5px"}
                        width={"800px"}
                        sx={{
                          background: theme.color?.secondaryLight,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {iconMapping[
                            item.report_icon as keyof typeof iconMapping
                          ]
                            ? React.createElement(
                                iconMapping[
                                  item.report_icon as keyof typeof iconMapping
                                ],
                                { style: { marginRight: "32px" } }
                              )
                            : React.createElement(iconMapping["TopicIcon"], {
                                style: { marginRight: "32px" },
                              })}

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: theme?.font?.fontFamily.primary,
                              fontWeight: theme.font?.fontWeight.thick,
                            }}
                            style={{
                              paddingLeft: "32px",
                              borderLeft: `1.5px solid ${theme.color?.secondary}`,
                            }}
                          >
                            {item?.report_name}
                          </Typography>
                        </div>
                        <a
                          href={item.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <Button
                            sx={{
                              backgroundColor: theme.color?.secondaryLight,
                              color: theme.color?.secondary,
                            }}
                            endIcon={<ArrowForwardIosIcon />}
                          ></Button>
                        </a>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ))
            )}
          </Grid>
        )}
      </Grid>
      {showModal && (
        <BasicModal
          onCloseModule={fetchData}
          title={uploadError ? errorMessage : successMessage}
          success={!uploadError}
          setState={setShowModal}
        />
      )}
    </Grid>
  );
};

export default FinanceReports;
